/**
 * Booking Formatters
 * Centralized formatting and display logic
 */

import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { DEFAULT_TIMEZONE } from './bookingConstants.js';

/**
 * Format a person's email into a display name
 * e.g., "eliza.keffeler@westtn.consulting" -> "Eliza Keffeler"
 */
export function formatPersonName(email: string): string {
  return email
    .split('@')[0]
    .replace(/\./g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

/**
 * Format a time slot for display
 * e.g., "2024-01-15T10:00:00Z" -> "10:00 AM"
 */
export function formatSlotTime(isoString: string, timezone: string = DEFAULT_TIMEZONE): string {
  try {
    return formatInTimeZone(parseISO(isoString), timezone, 'h:mm a');
  } catch {
    return 'Invalid time';
  }
}

/**
 * Format a date for display
 * e.g., "2024-01-15T10:00:00Z" -> "Monday, January 15, 2024"
 */
export function formatSlotDate(isoString: string, timezone: string = DEFAULT_TIMEZONE): string {
  try {
    return formatInTimeZone(parseISO(isoString), timezone, 'EEEE, MMMM d, yyyy');
  } catch {
    return 'Invalid date';
  }
}

/**
 * Format a time range for display
 * e.g., "2024-01-15T10:00:00Z" to "2024-01-15T10:30:00Z" -> "10:00 AM - 10:30 AM"
 */
export function formatTimeRange(
  startIso: string,
  endIso: string,
  timezone: string = DEFAULT_TIMEZONE
): string {
  try {
    const start = formatInTimeZone(parseISO(startIso), timezone, 'h:mm a');
    const end = formatInTimeZone(parseISO(endIso), timezone, 'h:mm a');
    return `${start} - ${end}`;
  } catch {
    return 'Invalid time range';
  }
}

/**
 * Format field name for error display
 * e.g., "bookeeEmail" -> "Bookee Email"
 */
export function formatFieldName(field: string): string {
  if (field === 'general') return 'Error';
  return field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1');
}
